/**
 * Pre Onboarding InProgress — types and helpers.
 * List/detail data from GET /vendor-initiations/pre-onboarding/in-progress.
 */

import { STATUS_PENDING_REAPPROVAL } from '../../constants/financialApproval';
import { STATUS_NEEDS_MORE_INFORMATION } from '../../constants/rfp';
import { buildRfpContinueHref } from '../../utils/rfpNavigation';
import { VMS_UNTITLED_SERVICE_LABEL, vmsVendorInitiationDisplayLabel } from '../../utils/vmsServiceDisplayName';
import { buildOnboardingVendorPipelineHref } from './onboardingFlowUrls';

export type PreOnboardingStageKey =
  | 'rfp'
  | 'prospective_vendors'
  | 'vendor_proposals'
  | 'vendor_evaluation'
  | 'financial_approval';

export type JourneyStepKey =
  | 'vendor_initiation'
  | 'materiality_assessment'
  | 'rfp'
  | 'prospective_vendors_proposal'
  | 'vendor_evaluation'
  | 'financial_approval';

/** Journey tile glyphs: done (green ✓), awaiting approval (maroon ●), not completed yet (amber halo). */
export type JourneyStepVisual = 'completed' | 'sent_for_approval' | 'pending';

export interface PreOnboardingInProgressCard {
  id: number;
  serviceName: string;
  stage: PreOnboardingStageKey;
  type: string;
  initiatedBy: string;
  updatedAt: string;
  initiationThreadLabel: string;
  vendorInitiationId: number;
  initiationStatus: string;
  annexure7Status: string | null;
  journeyStepVisuals: JourneyStepVisual[];
  /** Financial approval workflow finished; ready for onboarding / registration. */
  financialApprovalComplete: boolean;
  /** Logged-in user owes an approval in this initiation (BH/SH/FA paths). */
  pendingMyApproval: boolean;
  /** Service appears on the outsourcing register (read-only journey for approver trackers). */
  onOutsourcingRegister: boolean;
  vmsRegisteredVendorId: number | null;
  vmsOutsourcingVendorId: number | null;
  /**
   * This initiation’s current journey step is waiting on approvals (any participant).
   * Prefer {@link PreOnboardingInProgressApiRow.journey_step_visuals} (`sent_for_approval`) when present;
   * otherwise derived from pipeline to match backend journey logic.
   */
  workflowAwaitingApproval: boolean;
  pipeline: {
    approvedRfpCount: number;
    pvSubmittedCount: number;
    vpSubmittedCount: number;
    veApprovedCount: number;
    /** Latest evaluation for journey deep-link after submit. */
    vendorEvaluationId: number | null;
    vendorEvaluationOverallStatus: string | null;
    pendingApprovalRfpCount: number;
    /** Latest RFP in pending approval (journey deep-link). */
    pendingApprovalRfpId: number | null;
    /** Latest approved RFP (journey deep-link when RFP step is done). */
    approvedRfpId: number | null;
    /** Latest RFP for this service (draft, pending, or approved). */
    rfpId: number | null;
    rfpStatus: string | null;
    prospectiveVendorDraftId: number | null;
    /** Latest submitted prospective-vendor request (view-only journey deep-link). */
    prospectiveVendorSubmittedId: number | null;
    vpPendingApprovalCount: number;
    /** Latest non-draft vendor proposal (view-only journey deep-link). */
    vendorProposalRequestId: number | null;
    veAwaitingApprovalCount: number;
    faPendingAtApproverCount: number;
    faFullyApprovedCount: number;
    financialApprovalDraftId: number | null;
    financialApprovalId: number | null;
    financialApprovalStatus: string | null;
  };
}

export interface PreOnboardingInProgressApiRow {
  id: number;
  vendor_initiation_id: number;
  service_name: string;
  service_type: string | null;
  estimated_cost: number | string | null;
  currency: string | null;
  initiation_status: string;
  annexure_7_status: string | null;
  current_stage: PreOnboardingStageKey;
  journey_step_visuals: string[];
  /** Present when backend supports combined pre-onboarding + approved-FA rows. */
  financial_approval_complete?: boolean;
  /** True when the current journey step is waiting on approvals (any participant). */
  workflow_awaiting_approval?: boolean;
  /** Listing user owes an approval on this initiation. */
  pending_my_approval?: boolean | number;
  on_outsourcing_register?: boolean;
  vms_registered_vendor_id?: number | null;
  vms_outsourcing_vendor_id?: number | null;
  pipeline?: {
    approved_rfp_count: number;
    pv_submitted_count: number;
    vp_submitted_count: number;
    ve_approved_count: number;
    vendor_evaluation_id?: number | null;
    vendor_evaluation_overall_status?: string | null;
    pending_approval_rfp_count?: number;
    pending_approval_rfp_id?: number | null;
    approved_rfp_id?: number | null;
    rfp_id?: number | null;
    rfp_status?: string | null;
    prospective_vendor_draft_id?: number | null;
    vp_pending_approval_count?: number;
    ve_awaiting_approval_count?: number;
    fa_pending_at_approver_count?: number;
    fa_fully_approved_count?: number;
    financial_approval_draft_id?: number | null;
    financial_approval_id?: number | null;
    financial_approval_status?: string | null;
  };
  created_at: string;
  updated_at: string;
  created_by_first_name: string | null;
  created_by_last_name: string | null;
  requesting_department_id?: number | null;
  requesting_department_name?: string | null;
}

/**
 * Normalize GET pre-onboarding/in-progress envelopes so we always map real row objects
 * (some gateways return `{ data: { rows } }` or the array at the root).
 */
/** Read API booleans that may arrive as true, 1, or "true" / "1" (MySQL / serializers). */
export function readPreOnboardingApiBoolean(row: unknown, ...keys: string[]): boolean {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return false;
  const o = row as Record<string, unknown>;
  for (const key of keys) {
    const v = o[key];
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (s === 'true' || s === '1') return true;
    }
  }
  return false;
}

function recordLooksLikePreOnboardingApiRow(o: Record<string, unknown>): boolean {
  return (
    o.service_name != null ||
    o.serviceName != null ||
    o.vendor_initiation_id != null ||
    o.vendorInitiationId != null
  );
}

/** Peel `{ api: { … } }` / list-item shells until we reach the real GET row object. */
export function unwrapPreOnboardingApiRow(raw: unknown): PreOnboardingInProgressApiRow {
  let current: unknown = raw;
  for (let depth = 0; depth < 6; depth += 1) {
    if (current == null || typeof current !== 'object' || Array.isArray(current)) break;
    const o = current as Record<string, unknown>;
    if (o.card != null && o.api != null && typeof o.api === 'object' && !Array.isArray(o.api)) {
      current = o.api;
      continue;
    }
    if (recordLooksLikePreOnboardingApiRow(o)) {
      return current as PreOnboardingInProgressApiRow;
    }
    if (o.api != null && typeof o.api === 'object' && !Array.isArray(o.api)) {
      current = o.api;
      continue;
    }
    break;
  }
  return (raw ?? {}) as PreOnboardingInProgressApiRow;
}

export function buildPreOnboardingInProgressListItem(
  raw: unknown,
): PreOnboardingInProgressListItem {
  const api = unwrapPreOnboardingApiRow(raw);
  return {
    api,
    card: mapPreOnboardingApiRowToCard(api),
  };
}

/** Prefer `response.data` array from GET pre-onboarding/in-progress. */
export function coalescePreOnboardingInProgressListRows(res: unknown): PreOnboardingInProgressApiRow[] {
  if (res == null) return [];
  if (Array.isArray(res)) return res as PreOnboardingInProgressApiRow[];
  if (typeof res !== 'object') return [];
  const o = res as Record<string, unknown>;
  const d = o.data;
  if (Array.isArray(d)) return d as PreOnboardingInProgressApiRow[];
  if (d != null && typeof d === 'object' && !Array.isArray(d)) {
    const inner = d as Record<string, unknown>;
    if (Array.isArray(inner.data)) return inner.data as PreOnboardingInProgressApiRow[];
    if (Array.isArray(inner.rows)) return inner.rows as PreOnboardingInProgressApiRow[];
    if (Array.isArray(inner.items)) return inner.items as PreOnboardingInProgressApiRow[];
    if (Array.isArray(inner.list)) return inner.list as PreOnboardingInProgressApiRow[];
  }
  if (Array.isArray(o.rows)) return o.rows as PreOnboardingInProgressApiRow[];
  return [];
}

/** List page row: mapped `card` + raw `api` from GET pre-onboarding/in-progress. */
export type PreOnboardingInProgressListItem = {
  card: PreOnboardingInProgressCard;
  api: PreOnboardingInProgressApiRow;
};

/** Reads NMI from mapped card or raw API pipeline (list safety net). */
export function getPreOnboardingListItemNeedsMoreInformation(
  item: Pick<PreOnboardingInProgressListItem, 'api' | 'card'>,
): boolean {
  if (listCardShowsNeedsMoreInformationChip(item.card)) return true;
  const rawDict = item.api as unknown as ApiDict;
  const widened = widenApiDict(unwrapPreOnboardingApiRow(item.api) as unknown as ApiDict);
  const p = { ...readPipelineObject(widened), ...readPipelineObject(rawDict) };
  const nmi = STATUS_NEEDS_MORE_INFORMATION;
  const fa = p['financial_approval_status'] ?? p['financialApprovalStatus'];
  const ve = p['vendor_evaluation_overall_status'] ?? p['vendorEvaluationOverallStatus'];
  return (
    String(fa ?? '').toLowerCase() === nmi || String(ve ?? '').toLowerCase() === nmi
  );
}

/** Pending-approval chip — from `item.api.workflow_awaiting_approval` (unwrapped GET row). */
export function getPreOnboardingListItemWorkflowAwaitingApproval(
  item: Pick<PreOnboardingInProgressListItem, 'api' | 'card'>,
): boolean {
  if (getPreOnboardingListItemNeedsMoreInformation(item)) return false;
  const api = unwrapPreOnboardingApiRow(item.api);
  if (
    readPreOnboardingApiBoolean(api, 'workflow_awaiting_approval', 'workflowAwaitingApproval')
  ) {
    return true;
  }
  return Boolean(item.card.workflowAwaitingApproval && !item.card.financialApprovalComplete);
}

/** Whether the listing user owes an approval — from `item.api.pending_my_approval`. */
export function getPreOnboardingListItemPendingMyApproval(
  item: Pick<PreOnboardingInProgressListItem, 'api' | 'card'>,
): boolean {
  const api = unwrapPreOnboardingApiRow(item.api);
  return readPreOnboardingApiBoolean(api, 'pending_my_approval', 'pendingMyApproval');
}

export const STAGE_LABEL: Record<PreOnboardingStageKey, string> = {
  rfp: 'RFP',
  prospective_vendors: 'Prospective vendors',
  vendor_proposals: 'Vendor proposals',
  vendor_evaluation: 'Vendor evaluation',
  financial_approval: 'Financial approval',
};

/** Full journey grid (reference UI). */
export const JOURNEY_STEPS: {
  key: JourneyStepKey;
  title: string;
  description: string;
}[] = [
  {
    key: 'vendor_initiation',
    title: 'Vendor Initiation',
    description: 'Vendor request submitted successfully and moved for further processing.',
  },
  {
    key: 'materiality_assessment',
    title: 'Materiality Assessment',
    description:
      'Determine vendor criticality, business impact, and overall associated risk exposure.',
  },
  {
    key: 'rfp',
    title: 'RFP',
    description: 'Define requirements and invite vendors to submit detailed proposals.',
  },
  {
    key: 'prospective_vendors_proposal',
    title: 'Prospective Vendors & Vendor Proposal',
    description: 'Review and shortlist potential vendors based on eligibility and capabilities.',
  },
  {
    key: 'vendor_evaluation',
    title: 'Vendor Evaluation',
    description:
      'Assess vendor performance, compliance, risks, and operational suitability thoroughly.',
  },
  {
    key: 'financial_approval',
    title: 'Financial Approval',
    description: 'Review budget impact and approve financial terms for vendor engagement.',
  },
];

/** Short labels for list-card journey strip (same order as {@link JOURNEY_STEPS}). */
export const JOURNEY_STEP_STRIP_ABBR: readonly string[] = ['VI', 'MA', 'RFP', 'PV·VP', 'VE', 'FA'];

type ApiDict = Record<string, unknown>;

function isPlainApiObject(raw: unknown): raw is ApiDict {
  if (raw == null || typeof raw !== 'object') return false;
  if (Array.isArray(raw)) return false;
  if (ArrayBuffer.isView(raw)) return false;
  const proto = Object.getPrototypeOf(raw);
  return proto === Object.prototype || proto === null;
}

/** Same keys as API `pipeline` object; used to merge flat row fields when `pipeline` is missing or empty. */
const PIPELINE_FIELD_PAIRS: readonly [snake: string, camel: string][] = [
  ['approved_rfp_count', 'approvedRfpCount'],
  ['pv_submitted_count', 'pvSubmittedCount'],
  ['vp_submitted_count', 'vpSubmittedCount'],
  ['ve_approved_count', 'veApprovedCount'],
  ['vendor_evaluation_id', 'vendorEvaluationId'],
  ['vendor_evaluation_overall_status', 'vendorEvaluationOverallStatus'],
  ['pending_approval_rfp_count', 'pendingApprovalRfpCount'],
  ['pending_approval_rfp_id', 'pendingApprovalRfpId'],
  ['approved_rfp_id', 'approvedRfpId'],
  ['rfp_id', 'rfpId'],
  ['rfp_status', 'rfpStatus'],
  ['prospective_vendor_draft_id', 'prospectiveVendorDraftId'],
  ['prospective_vendor_submitted_id', 'prospectiveVendorSubmittedId'],
  ['vendor_proposal_request_id', 'vendorProposalRequestId'],
  ['vp_pending_approval_count', 'vpPendingApprovalCount'],
  ['ve_awaiting_approval_count', 'veAwaitingApprovalCount'],
  ['fa_pending_at_approver_count', 'faPendingAtApproverCount'],
  ['fa_fully_approved_count', 'faFullyApprovedCount'],
  ['financial_approval_draft_id', 'financialApprovalDraftId'],
  ['financial_approval_id', 'financialApprovalId'],
  ['financial_approval_status', 'financialApprovalStatus'],
];

function parseJsonObject(raw: unknown): ApiDict | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      const o = JSON.parse(t) as unknown;
      return isPlainApiObject(o) ? o : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && isPlainApiObject(raw)) return raw;
  return null;
}

/**
 * API may return `journey_step_visuals` as an array, JSON string, or plain object with numeric keys
 * (some serializers emit `{ "0": "completed", "1": "completed", … }` instead of an array).
 */
function coalesceJsonArray(raw: unknown): unknown[] | null {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      const o = JSON.parse(t) as unknown;
      if (Array.isArray(o)) return o;
      if (o != null && typeof o === 'object' && !Array.isArray(o)) {
        return objectWithNumericKeysToArray(o as Record<string, unknown>);
      }
      return null;
    } catch {
      return null;
    }
  }
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return objectWithNumericKeysToArray(raw as Record<string, unknown>);
  }
  return null;
}

function objectWithNumericKeysToArray(o: Record<string, unknown>): unknown[] | null {
  const keys = Object.keys(o);
  if (keys.length === 0) return null;
  if (!keys.every(k => /^\d+$/.test(k))) return null;
  return keys.sort((a, b) => Number(a) - Number(b)).map(k => o[k]);
}

/** Shallow-merge; do not overwrite with `undefined` (ORM shells often enumerate `pipeline: undefined`). */
function mergeDefinedFields(base: ApiDict, patch: ApiDict): ApiDict {
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** Merge ORM / JSON:API shapes so `pipeline`, `journey_step_visuals`, and counts stay visible. */
function widenApiDict(row: ApiDict): ApiDict {
  const layers: ApiDict[] = [];
  if (isPlainApiObject(row.dataValues)) layers.push(row.dataValues as ApiDict);
  if (isPlainApiObject(row.attributes)) layers.push(row.attributes as ApiDict);
  if (typeof row.toJSON === 'function') {
    try {
      const j = row.toJSON() as unknown;
      if (isPlainApiObject(j)) layers.push(j);
    } catch {
      /* ignore */
    }
  }
  layers.push(row);
  return layers.reduce((acc, layer) => mergeDefinedFields(acc, layer), {} as ApiDict);
}

function normalizeJourneyVisualToken(v: unknown): string {
  return String(v)
    .toLowerCase()
    .trim()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');
}

function journeyTokenIsSentForApproval(v: unknown): boolean {
  return normalizeJourneyVisualToken(v) === 'sent_for_approval';
}

/** Legacy list payloads used `current` for the active step (same UX as `sent_for_approval`). */
function journeyTokenIsLegacyCurrentStep(v: unknown): boolean {
  return normalizeJourneyVisualToken(v) === 'current';
}

function journeyTokenIndicatesAwaitingApproval(v: unknown): boolean {
  return journeyTokenIsSentForApproval(v) || journeyTokenIsLegacyCurrentStep(v);
}

function readPipelineObject(row: ApiDict): ApiDict {
  const nested =
    parseJsonObject(row.pipeline) ??
    parseJsonObject(row.Pipeline) ??
    (() => {
      const raw = row.pipeline ?? row.Pipeline;
      if (raw == null) return null;
      if (isPlainApiObject(raw)) return raw;
      return null;
    })();

  const base = nested ?? {};
  const merged: ApiDict = { ...base };
  for (const [snake, camel] of PIPELINE_FIELD_PAIRS) {
    const nestedVal = merged[snake] ?? merged[camel];
    if (nestedVal !== undefined && nestedVal !== null) continue;
    const fromRoot = row[snake] ?? row[camel];
    if (fromRoot !== undefined && fromRoot !== null) merged[snake] = fromRoot;
  }
  return merged;
}

function pNum(p: ApiDict, snake: string, camel: string): number {
  const v = p[snake] ?? p[camel];
  if (typeof v === 'boolean') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pOptionalPositiveId(p: ApiDict, snake: string, camel: string): number | null {
  const v = p[snake] ?? p[camel];
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function parseJourneyStepVisualsFromApi(raw: unknown): JourneyStepVisual[] {
  const arr = Array.isArray(raw) ? raw : coalesceJsonArray(raw);
  if (arr == null || arr.length === 0) return [];
  let norm = arr.slice();
  if (norm.length > JOURNEY_STEPS.length) norm = norm.slice(0, JOURNEY_STEPS.length);
  while (norm.length < JOURNEY_STEPS.length) norm.push('pending');
  const out: JourneyStepVisual[] = [];
  for (const v of norm) {
    const s = normalizeJourneyVisualToken(v);
    if (s === 'completed') out.push('completed');
    else if (s === 'sent_for_approval' || s === 'current') out.push('sent_for_approval');
    else if (s === 'pending') out.push('pending');
    else out.push('pending');
  }
  return out;
}

/** Only trust API journey glyphs when the raw array is full-length and uses current tokens (not legacy `current`). */
function apiJourneyStepVisualsAreTrusted(raw: unknown): boolean {
  const arr = coalesceJsonArray(raw);
  if (arr == null || arr.length !== JOURNEY_STEPS.length) return false;
  return arr.every(v => {
    const s = normalizeJourneyVisualToken(v);
    return s === 'completed' || s === 'sent_for_approval' || s === 'pending';
  });
}

/**
 * Same inputs as {@link mapPreOnboardingApiRowToCard}. Used as a safety net in the list page so the
 * “Pending approval” chip still appears if the mapped card dropped flags (journey length drift, ORM shells, etc.).
 */
export function extractRawPendingApprovalSignals(raw: unknown): boolean {
  const rawDict = raw as ApiDict;
  if (rawDict == null || typeof rawDict !== 'object') return false;
  const widened = widenApiDict(rawDict);
  const R = mergeDefinedFields(widened, rawDict);
  const p = { ...readPipelineObject(widened), ...readPipelineObject(rawDict) };

  const faComplete =
    R.financial_approval_complete === true ||
    R.financialApprovalComplete === true ||
    pNum(p, 'fa_fully_approved_count', 'faFullyApprovedCount') >= 1;
  if (faComplete) return false;

  const nmi = STATUS_NEEDS_MORE_INFORMATION;
  const faSt = p['financial_approval_status'] ?? p['financialApprovalStatus'];
  const veSt = p['vendor_evaluation_overall_status'] ?? p['vendorEvaluationOverallStatus'];
  if (String(faSt ?? '').toLowerCase() === nmi || String(veSt ?? '').toLowerCase() === nmi) {
    return false;
  }

  const pendingMy =
    R.pending_my_approval === true ||
    Number(R.pending_my_approval) === 1 ||
    R.pendingMyApproval === true ||
    Number(R.pendingMyApproval) === 1;
  if (pendingMy) return true;

  if (
    R.workflow_awaiting_approval === true ||
    R.workflowAwaitingApproval === true
  ) {
    return true;
  }

  if (
    pNum(p, 'pending_approval_rfp_count', 'pendingApprovalRfpCount') >= 1 ||
    pNum(p, 'vp_pending_approval_count', 'vpPendingApprovalCount') >= 1 ||
    pNum(p, 've_awaiting_approval_count', 'veAwaitingApprovalCount') >= 1 ||
    pNum(p, 'fa_pending_at_approver_count', 'faPendingAtApproverCount') >= 1
  ) {
    return true;
  }

  const journeyRaw = (R.journey_step_visuals ?? R.journeyStepVisuals) as unknown;
  const journeyArray = coalesceJsonArray(journeyRaw);
  if (journeyArray != null && journeyArray.some(v => journeyTokenIndicatesAwaitingApproval(v))) {
    return true;
  }

  return String(R.annexure_7_status ?? R.annexure7Status ?? '').toLowerCase() === 'pending';
}

export function mapPreOnboardingApiRowToCard(row: PreOnboardingInProgressApiRow): PreOnboardingInProgressCard {
  const rawDict = row as unknown as ApiDict;
  const widened = widenApiDict(rawDict);
  /** Axios JSON must win over ORM shells so `pipeline` / `journey_step_visuals` are never dropped. */
  const R = mergeDefinedFields(widened, rawDict);
  const p = { ...readPipelineObject(widened), ...readPipelineObject(rawDict) };

  const viIdRaw = R.vendor_initiation_id ?? R.vendorInitiationId ?? R.id;
  let viId =
    typeof viIdRaw === 'number' && Number.isFinite(viIdRaw)
      ? viIdRaw
      : parseInt(String(viIdRaw ?? ''), 10);
  if (!Number.isFinite(viId) || viId < 1) {
    viId = Number(row.id);
  }

  const firstName = (R.created_by_first_name ?? R.createdByFirstName) as string | null | undefined;
  const lastName = (R.created_by_last_name ?? R.createdByLastName) as string | null | undefined;
  const initiatedBy = [firstName, lastName].filter(Boolean).join(' ') || '—';

  const serviceRaw = (R.service_name ?? R.serviceName) as string | undefined;
  const serviceName = vmsVendorInitiationDisplayLabel(serviceRaw, VMS_UNTITLED_SERVICE_LABEL);

  const faComplete =
    R.financial_approval_complete === true ||
    R.financialApprovalComplete === true ||
    pNum(p, 'fa_fully_approved_count', 'faFullyApprovedCount') >= 1;

  const journeyRaw = (R.journey_step_visuals ?? R.journeyStepVisuals) as unknown;
  const journeyArray = coalesceJsonArray(journeyRaw);
  const journeyStepVisuals = parseJourneyStepVisualsFromApi(journeyArray ?? journeyRaw);
  const workflowAwaitingFromApi = readPreOnboardingApiBoolean(
    R,
    'workflow_awaiting_approval',
    'workflowAwaitingApproval',
  );
  const awaitingFromApiJourneyVisuals =
    !faComplete &&
    (workflowAwaitingFromApi ||
      (journeyArray != null && journeyArray.some(v => journeyTokenIndicatesAwaitingApproval(v))));

  const annex7 = (R.annexure_7_status ?? R.annexure7Status) as string | null | undefined;
  const currentStage = (R.current_stage ?? R.currentStage) as PreOnboardingStageKey;
  const initiationStatus = String(R.initiation_status ?? R.initiationStatus ?? 'approved');
  const serviceType = (R.service_type ?? R.serviceType) as string | null | undefined;
  const updatedAt = String(R.updated_at ?? R.updatedAt ?? R.created_at ?? R.createdAt ?? '');

  const pendingRaw = R.pending_my_approval ?? R.pendingMyApproval;

  const card: PreOnboardingInProgressCard = {
    id: viId,
    vendorInitiationId: viId,
    serviceName,
    stage: currentStage,
    type: String(serviceType ?? '').trim() || '—',
    initiatedBy,
    updatedAt,
    initiationThreadLabel: serviceName,
    initiationStatus,
    annexure7Status: annex7 != null ? String(annex7) : null,
    journeyStepVisuals,
    financialApprovalComplete: faComplete,
    pendingMyApproval:
      Boolean(pendingRaw === true || Number(pendingRaw) === 1) && !faComplete,
    onOutsourcingRegister:
      R.on_outsourcing_register === true ||
      R.onOutsourcingRegister === true ||
      Number(R.on_outsourcing_register) === 1,
    vmsRegisteredVendorId: pOptionalPositiveId(R, 'vms_registered_vendor_id', 'vmsRegisteredVendorId'),
    vmsOutsourcingVendorId: pOptionalPositiveId(R, 'vms_outsourcing_vendor_id', 'vmsOutsourcingVendorId'),
    workflowAwaitingApproval: false,
    pipeline: {
      approvedRfpCount: pNum(p, 'approved_rfp_count', 'approvedRfpCount'),
      pvSubmittedCount: pNum(p, 'pv_submitted_count', 'pvSubmittedCount'),
      vpSubmittedCount: pNum(p, 'vp_submitted_count', 'vpSubmittedCount'),
      veApprovedCount: pNum(p, 've_approved_count', 'veApprovedCount'),
      vendorEvaluationId: pOptionalPositiveId(p, 'vendor_evaluation_id', 'vendorEvaluationId'),
      vendorEvaluationOverallStatus: (() => {
        const raw = p['vendor_evaluation_overall_status'] ?? p['vendorEvaluationOverallStatus'];
        if (raw == null || raw === '') return null;
        return String(raw);
      })(),
      pendingApprovalRfpCount: pNum(p, 'pending_approval_rfp_count', 'pendingApprovalRfpCount'),
      pendingApprovalRfpId:
        pNum(p, 'pending_approval_rfp_count', 'pendingApprovalRfpCount') >= 1
          ? pOptionalPositiveId(p, 'pending_approval_rfp_id', 'pendingApprovalRfpId')
          : null,
      approvedRfpId:
        pNum(p, 'approved_rfp_count', 'approvedRfpCount') >= 1
          ? pOptionalPositiveId(p, 'approved_rfp_id', 'approvedRfpId')
          : null,
      rfpId: pOptionalPositiveId(p, 'rfp_id', 'rfpId'),
      rfpStatus: (() => {
        const raw = p['rfp_status'] ?? p['rfpStatus'];
        if (raw == null || raw === '') return null;
        return String(raw);
      })(),
      prospectiveVendorDraftId: pOptionalPositiveId(
        p,
        'prospective_vendor_draft_id',
        'prospectiveVendorDraftId',
      ),
      prospectiveVendorSubmittedId: pOptionalPositiveId(
        p,
        'prospective_vendor_submitted_id',
        'prospectiveVendorSubmittedId',
      ),
      vendorProposalRequestId: pOptionalPositiveId(
        p,
        'vendor_proposal_request_id',
        'vendorProposalRequestId',
      ),
      vpPendingApprovalCount: pNum(p, 'vp_pending_approval_count', 'vpPendingApprovalCount'),
      veAwaitingApprovalCount: pNum(p, 've_awaiting_approval_count', 'veAwaitingApprovalCount'),
      faPendingAtApproverCount: pNum(p, 'fa_pending_at_approver_count', 'faPendingAtApproverCount'),
      faFullyApprovedCount: pNum(p, 'fa_fully_approved_count', 'faFullyApprovedCount'),
      financialApprovalDraftId: pOptionalPositiveId(
        p,
        'financial_approval_draft_id',
        'financialApprovalDraftId',
      ),
      financialApprovalId: pOptionalPositiveId(p, 'financial_approval_id', 'financialApprovalId'),
      financialApprovalStatus: (() => {
        const raw = p['financial_approval_status'] ?? p['financialApprovalStatus'];
        if (raw == null || raw === '') return null;
        return String(raw);
      })(),
    },
  };

  card.workflowAwaitingApproval =
    !faComplete &&
    (workflowAwaitingFromApi ||
      awaitingFromApiJourneyVisuals ||
      journeyStepVisuals.some(v => v === 'sent_for_approval') ||
      preOnboardingCardHasAnyApproverQueue(card) ||
      isPreOnboardingWorkflowAwaitingApproval(card));

  return card;
}

/**
 * List-card “Pending approval” chip — not limited to {@link PreOnboardingInProgressCard.pendingMyApproval}.
 * `pending_my_approval: false` only means the logged-in user is not the approver; the chip still shows
 * when `workflow_awaiting_approval`, journey `sent_for_approval`, or pipeline queues say otherwise.
 */
/** Journey progress tile — same signal as maroon “sent for approval” glyph. */
export function journeyStepShowsPendingApprovalChip(visual: JourneyStepVisual): boolean {
  return visual === 'sent_for_approval';
}

/** True when no financial approval draft/request exists yet for this service. */
export function isFinancialApprovalNotStarted(
  card: Pick<PreOnboardingInProgressCard, 'pipeline' | 'financialApprovalComplete'>,
): boolean {
  if (card.financialApprovalComplete) return false;
  const p = card.pipeline;
  if (p.financialApprovalId != null || p.financialApprovalDraftId != null) return false;
  if (p.faPendingAtApproverCount >= 1 || p.faFullyApprovedCount >= 1) return false;
  const st = String(p.financialApprovalStatus ?? '').trim();
  return st === '';
}

/**
 * Whether this journey stage has no draft/submission yet (vs completed or in progress).
 * Vendor Initiation is never “not started” on this list — the initiation already exists.
 */
export function isJourneyStepNotStarted(
  stepKey: JourneyStepKey,
  card: Pick<
    PreOnboardingInProgressCard,
    'pipeline' | 'financialApprovalComplete' | 'annexure7Status'
  >,
): boolean {
  if (card.financialApprovalComplete) return false;
  const p = card.pipeline;
  switch (stepKey) {
    case 'vendor_initiation':
      return false;
    case 'materiality_assessment':
      return String(card.annexure7Status ?? '').trim() === '';
    case 'rfp': {
      if (p.rfpId != null || p.approvedRfpId != null || p.pendingApprovalRfpId != null) return false;
      if (p.approvedRfpCount >= 1 || p.pendingApprovalRfpCount >= 1) return false;
      return String(p.rfpStatus ?? '').trim() === '';
    }
    case 'prospective_vendors_proposal': {
      if (p.prospectiveVendorDraftId != null || p.prospectiveVendorSubmittedId != null) return false;
      if (p.vendorProposalRequestId != null) return false;
      if (p.pvSubmittedCount >= 1 || p.vpSubmittedCount >= 1 || p.vpPendingApprovalCount >= 1) {
        return false;
      }
      return true;
    }
    case 'vendor_evaluation': {
      if (p.vendorEvaluationId != null) return false;
      if (p.veApprovedCount >= 1 || p.veAwaitingApprovalCount >= 1) return false;
      return String(p.vendorEvaluationOverallStatus ?? '').trim() === '';
    }
    case 'financial_approval':
      return isFinancialApprovalNotStarted(card);
    default:
      return false;
  }
}

/**
 * Journey tile — show “Not started” when the stage has no work yet
 * (amber pending glyph only; no pending-approval / NMI / re-approval chip).
 */
export function journeyStepShowsNotStartedChip(
  stepKey: JourneyStepKey,
  card: Pick<
    PreOnboardingInProgressCard,
    'pipeline' | 'financialApprovalComplete' | 'annexure7Status'
  >,
  visual: JourneyStepVisual,
): boolean {
  if (visual !== 'pending') return false;
  return isJourneyStepNotStarted(stepKey, card);
}

/**
 * Business owners cannot skip ahead on the journey grid. Returns a toast message when blocked.
 */
export function getBusinessOwnerJourneyAheadBlockMessage(
  stepIndex: number,
  currentStepIndex: number,
): string | null {
  if (stepIndex <= currentStepIndex) return null;
  const previous = JOURNEY_STEPS[stepIndex - 1];
  const target = JOURNEY_STEPS[stepIndex];
  if (!previous || !target) return null;
  return `Complete "${previous.title}" before opening "${target.title}".`;
}

export function listCardShowsPendingApprovalChip(row: PreOnboardingInProgressCard): boolean {
  if (isFinancialApprovalReapprovalInProgress(row)) return true;
  if (row.financialApprovalComplete) return false;
  if (isVendorInitiationPendingAnnexureApprovalStatus(row.initiationStatus)) return true;
  if (listCardShowsNeedsMoreInformationChip(row)) return false;
  if (row.pendingMyApproval || row.workflowAwaitingApproval) return true;
  if (preOnboardingCardHasAnyApproverQueue(row)) return true;
  if (row.journeyStepVisuals.some(v => v === 'sent_for_approval')) return true;
  if (String(row.annexure7Status ?? '').toLowerCase() === 'pending') return true;
  return false;
}

/** Returned to creator for more information (financial approval or vendor evaluation). */
export function listCardShowsNeedsMoreInformationChip(row: PreOnboardingInProgressCard): boolean {
  if (row.financialApprovalComplete) return false;
  const nmi = STATUS_NEEDS_MORE_INFORMATION;
  const p = row.pipeline;
  if (String(p.financialApprovalStatus ?? '').toLowerCase() === nmi) return true;
  if (String(p.vendorEvaluationOverallStatus ?? '').toLowerCase() === nmi) return true;
  return false;
}

export function journeyStepShowsNeedsMoreInformationChip(
  stepKey: JourneyStepKey,
  card: Pick<PreOnboardingInProgressCard, 'pipeline' | 'financialApprovalComplete'>,
): boolean {
  if (card.financialApprovalComplete) return false;
  const nmi = STATUS_NEEDS_MORE_INFORMATION;
  const p = card.pipeline;
  if (stepKey === 'financial_approval') {
    return String(p.financialApprovalStatus ?? '').toLowerCase() === nmi;
  }
  if (stepKey === 'vendor_evaluation') {
    return String(p.vendorEvaluationOverallStatus ?? '').toLowerCase() === nmi;
  }
  return false;
}

/** First FA approved (`financialApprovalComplete`) but a post-approval amend is under review. */
export function isFinancialApprovalReapprovalInProgress(
  card: Pick<PreOnboardingInProgressCard, 'pipeline' | 'financialApprovalComplete'>,
): boolean {
  if (!card.financialApprovalComplete) return false;
  const st = String(card.pipeline.financialApprovalStatus ?? '').toLowerCase();
  if (st === STATUS_PENDING_REAPPROVAL) return true;
  if (st === STATUS_NEEDS_MORE_INFORMATION) return false;
  return card.pipeline.faPendingAtApproverCount >= 1;
}

export function journeyStepShowsReapprovalInProgressChip(
  stepKey: JourneyStepKey,
  card: Pick<PreOnboardingInProgressCard, 'pipeline' | 'financialApprovalComplete'>,
): boolean {
  return stepKey === 'financial_approval' && isFinancialApprovalReapprovalInProgress(card);
}

/** True when any module has rows waiting on approvers (list-card badge; not limited to “current” index). */
export function preOnboardingCardHasAnyApproverQueue(
  card: Pick<PreOnboardingInProgressCard, 'pipeline' | 'financialApprovalComplete'>,
): boolean {
  if (card.financialApprovalComplete) return false;
  const f = card.pipeline;
  return (
    f.pendingApprovalRfpCount >= 1 ||
    f.vpPendingApprovalCount >= 1 ||
    f.veAwaitingApprovalCount >= 1 ||
    f.faPendingAtApproverCount >= 1
  );
}

/** @deprecated Prefer {@link listCardShowsPendingApprovalChip} for list UI. */
export function shouldShowPreOnboardingListPendingApprovalBadge(row: PreOnboardingInProgressCard): boolean {
  return listCardShowsPendingApprovalChip(row);
}

/** True only for unsaved-as-submitted vendor initiation drafts (additive admin list UX). */
function isVendorInitiationDraftStatus(status: string | null | undefined): boolean {
  return String(status ?? '').trim().toLowerCase() === 'draft';
}

export function isVendorInitiationPendingApprovalStatus(status: string | null | undefined): boolean {
  return String(status ?? '').trim().toLowerCase() === 'pending';
}

/** Submitted initiation awaiting parallel Annexure 7 (materiality) approvers. */
export function isVendorInitiationPendingAnnexureApprovalStatus(
  status: string | null | undefined,
): boolean {
  return String(status ?? '').trim().toLowerCase() === 'pending_annexure_approval';
}

/** Aligns with backend `resolveCurrentJourneyStepIndex`. Draft → step 0 only; approved path unchanged. */
export function resolveCurrentJourneyStepIndexFromCard(
  card: Pick<PreOnboardingInProgressCard, 'annexure7Status' | 'pipeline' | 'financialApprovalComplete'> & {
    initiationStatus?: string;
  },
): number {
  if (card.financialApprovalComplete) return 5;
  // Additive only — approved / NMI initiations keep the pre-existing index rules below.
  if (isVendorInitiationDraftStatus(card.initiationStatus)) return 0;
  if (isVendorInitiationPendingApprovalStatus(card.initiationStatus)) return 0;
  if (isVendorInitiationPendingAnnexureApprovalStatus(card.initiationStatus)) return 1;
  const {
    approvedRfpCount,
    pvSubmittedCount,
    vpSubmittedCount,
    veApprovedCount,
    pendingApprovalRfpCount,
  } = card.pipeline;
  const ax7Approved = String(card.annexure7Status ?? '').toLowerCase() === 'approved';
  /** If A7 is not “approved” in the payload but downstream work exists, do not stay stuck on step 1 (MA). */
  const progressedPastMateriality =
    pendingApprovalRfpCount >= 1 ||
    approvedRfpCount >= 1 ||
    pvSubmittedCount >= 1 ||
    vpSubmittedCount >= 1 ||
    veApprovedCount >= 1;
  if (!ax7Approved && !progressedPastMateriality && approvedRfpCount < 1) return 1;
  if (approvedRfpCount < 1) return 2;
  if (pvSubmittedCount < 1) return 3;
  if (vpSubmittedCount < 1) return 3;
  if (veApprovedCount < 1) return 4;
  return 5;
}

function resolveCurrentStepAwaitingApprovalFromCard(
  currentIdx: number,
  card: Pick<PreOnboardingInProgressCard, 'annexure7Status' | 'pipeline'> & {
    initiationStatus?: string;
  },
): boolean {
  const ax = String(card.annexure7Status ?? '').toLowerCase();
  const f = card.pipeline;
  switch (currentIdx) {
    case 0:
      return isVendorInitiationPendingApprovalStatus(card.initiationStatus);
    case 1:
      return ax === 'pending';
    case 2:
      return f.pendingApprovalRfpCount >= 1;
    case 3:
      return f.pvSubmittedCount >= 1 && f.vpSubmittedCount >= 1 && f.vpPendingApprovalCount >= 1;
    case 4: {
      if (
        String(f.vendorEvaluationOverallStatus ?? '').toLowerCase() ===
        STATUS_NEEDS_MORE_INFORMATION
      ) {
        return false;
      }
      return f.veAwaitingApprovalCount >= 1;
    }
    case 5: {
      if (String(f.financialApprovalStatus ?? '').toLowerCase() === STATUS_NEEDS_MORE_INFORMATION) {
        return false;
      }
      return (
        f.faPendingAtApproverCount >= 1 ||
        String(f.financialApprovalStatus ?? '').toLowerCase() === STATUS_PENDING_REAPPROVAL
      );
    }
    default:
      return false;
  }
}

/**
 * True when the current journey step is waiting on approvals somewhere in the workflow
 * (uses the same rules as journey “sent_for_approval”), not limited to the listing user.
 * Pair with {@link PreOnboardingInProgressCard.pendingMyApproval} for “your action” vs “others / queue”.
 */
export function isPreOnboardingWorkflowAwaitingApproval(
  card: Pick<PreOnboardingInProgressCard, 'annexure7Status' | 'pipeline' | 'financialApprovalComplete'> & {
    initiationStatus?: string;
  },
): boolean {
  if (isFinancialApprovalReapprovalInProgress(card)) return true;
  if (card.financialApprovalComplete) return false;
  const currentIdx = resolveCurrentJourneyStepIndexFromCard(card);
  return resolveCurrentStepAwaitingApprovalFromCard(currentIdx, card);
}

function journeyVisualsLookParsed(arr: JourneyStepVisual[] | null | undefined): boolean {
  if (arr == null || !Array.isArray(arr)) return false;
  return (
    arr.length === JOURNEY_STEPS.length &&
    arr.every(v => v === 'completed' || v === 'sent_for_approval' || v === 'pending')
  );
}

/** Prefer API `journey_step_visuals`; recompute if legacy (`current`), short arrays, or missing. */
export function resolveJourneyStepVisualsForCard(
  card: Pick<
    PreOnboardingInProgressCard,
    | 'journeyStepVisuals'
    | 'annexure7Status'
    | 'pipeline'
    | 'financialApprovalComplete'
  > & { initiationStatus?: string },
  options?: { rawJourneyStepVisuals?: unknown },
): JourneyStepVisual[] {
  if (card.financialApprovalComplete) {
    return JOURNEY_STEPS.map(() => 'completed');
  }
  if (journeyVisualsLookParsed(card.journeyStepVisuals)) {
    const hasAwaitingGlyph = card.journeyStepVisuals.some(v => v === 'sent_for_approval');
    const rawTrusted =
      options?.rawJourneyStepVisuals != null &&
      apiJourneyStepVisualsAreTrusted(options.rawJourneyStepVisuals);
    if (hasAwaitingGlyph || rawTrusted) {
      return card.journeyStepVisuals;
    }
  }
  const current = resolveCurrentJourneyStepIndexFromCard(card);
  const awaiting = resolveCurrentStepAwaitingApprovalFromCard(current, card);
  return JOURNEY_STEPS.map((_, i) => {
    if (i < current) return 'completed';
    if (i > current) return 'pending';
    return awaiting ? 'sent_for_approval' : 'pending';
  });
}

/** Uppercase label for list card “Current stage” pill — pre-onboarding journey steps only (through APPROVED). */
export function getPreOnboardingListCardStageBadgeText(row: PreOnboardingInProgressCard): string {
  if (row.financialApprovalComplete) return 'APPROVED';
  // Additive admin draft UX only — approved journeys keep stage labels below.
  if (isVendorInitiationDraftStatus(row.initiationStatus)) return 'DRAFT';
  const idx = resolveCurrentJourneyStepIndexFromCard(row);
  switch (idx) {
    case 0:
      return 'VENDOR INITIATION';
    case 1:
      return 'MATERIALITY ASSESSMENT';
    case 2:
      return 'RFP';
    case 3: {
      const { pvSubmittedCount, vpSubmittedCount } = row.pipeline;
      if (pvSubmittedCount < 1) return 'PROSPECTIVE VENDORS';
      if (vpSubmittedCount < 1) return 'VENDOR PROPOSALS';
      return 'PROSPECTIVE VENDORS & VENDOR PROPOSAL';
    }
    case 4:
      return 'VENDOR EVALUATION';
    case 5:
      return 'FINANCIAL APPROVAL';
    default:
      return STAGE_LABEL[row.stage].toUpperCase();
  }
}

/** Sentence-case title for tooltip / accessibility alongside {@link getPreOnboardingListCardStageBadgeText}. */
export function getPreOnboardingListCardStageBadgePlainTitle(row: PreOnboardingInProgressCard): string {
  if (row.financialApprovalComplete) return 'Pre-onboarding approved (financial approval complete)';
  if (isVendorInitiationDraftStatus(row.initiationStatus)) return 'Draft';
  const idx = resolveCurrentJourneyStepIndexFromCard(row);
  if (idx === 3) {
    const { pvSubmittedCount, vpSubmittedCount } = row.pipeline;
    if (pvSubmittedCount < 1) return 'Prospective Vendors';
    if (vpSubmittedCount < 1) return 'Vendor Proposals';
    return JOURNEY_STEPS[3].title;
  }
  if (idx >= 0 && idx < JOURNEY_STEPS.length) {
    return JOURNEY_STEPS[idx].title;
  }
  return STAGE_LABEL[row.stage];
}

/** Dashboard vendor tracker — column order matches {@link JOURNEY_STEPS}. */
export const PRE_ONBOARDING_DASHBOARD_TRACKER_COLUMNS: readonly {
  stepKey: JourneyStepKey;
  label: string;
}[] = [
  { stepKey: 'vendor_initiation', label: 'Initiation' },
  { stepKey: 'materiality_assessment', label: 'Materiality Assessment' },
  { stepKey: 'rfp', label: 'RFP' },
  { stepKey: 'prospective_vendors_proposal', label: 'Proposal Review' },
  { stepKey: 'vendor_evaluation', label: 'Vendor Evaluation' },
  { stepKey: 'financial_approval', label: 'Financial Approval' },
] as const;

export type PreOnboardingDashboardStepCellKind = 'completed' | 'in_progress' | 'not_started';

export type PreOnboardingDashboardOverallStatus =
  | 'Completed'
  | 'In Progress'
  | 'Pending Approval'
  | 'Draft';

export function resolvePreOnboardingDashboardStepCellKind(
  stepKey: JourneyStepKey,
  card: Pick<
    PreOnboardingInProgressCard,
    'pipeline' | 'financialApprovalComplete' | 'annexure7Status'
  >,
  visual: JourneyStepVisual,
): PreOnboardingDashboardStepCellKind {
  if (visual === 'completed') return 'completed';
  if (visual === 'sent_for_approval') return 'in_progress';
  if (isJourneyStepNotStarted(stepKey, card)) return 'not_started';
  return 'in_progress';
}

export function getPreOnboardingDashboardOverallStatus(
  card: Pick<
    PreOnboardingInProgressCard,
    | 'journeyStepVisuals'
    | 'annexure7Status'
    | 'pipeline'
    | 'financialApprovalComplete'
    | 'initiationStatus'
  >,
): PreOnboardingDashboardOverallStatus {
  if (isVendorInitiationDraftStatus(card.initiationStatus)) return 'Draft';
  if (
    isVendorInitiationPendingApprovalStatus(card.initiationStatus) ||
    isVendorInitiationPendingAnnexureApprovalStatus(card.initiationStatus)
  ) {
    return 'Pending Approval';
  }
  if (card.financialApprovalComplete) return 'Completed';
  const visuals = resolveJourneyStepVisualsForCard(card);
  const faVisual = visuals[5] ?? 'pending';
  if (faVisual === 'sent_for_approval') return 'Pending Approval';
  const currentIdx = resolveCurrentJourneyStepIndexFromCard(card);
  if (currentIdx === 5 && resolveCurrentStepAwaitingApprovalFromCard(currentIdx, card)) {
    return 'Pending Approval';
  }
  return 'In Progress';
}

export function readPreOnboardingRequestingDepartmentName(
  api: PreOnboardingInProgressApiRow | Record<string, unknown>,
): string {
  const o = api as Record<string, unknown>;
  const raw =
    o.requesting_department_name ??
    o.requestingDepartmentName ??
    o.department_name ??
    o.department;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return '—';
}

export function daysPendingSinceIso(iso: string | undefined): number {
  if (iso == null || !String(iso).trim()) return 0;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  const diffMs = Date.now() - d.getTime();
  return Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
}

/** Journey step index waiting on an external approval queue, if any. */
export function resolvePreOnboardingPendingActivityStepIndex(
  card: PreOnboardingInProgressCard,
  visuals: JourneyStepVisual[],
): number | null {
  if (card.financialApprovalComplete) return null;
  if (isVendorInitiationDraftStatus(card.initiationStatus)) return null;
  if (listCardShowsNeedsMoreInformationChip(card)) return null;

  const awaitingQueue =
    listCardShowsPendingApprovalChip(card) ||
    isPreOnboardingWorkflowAwaitingApproval(card) ||
    visuals.some((v) => v === 'sent_for_approval') ||
    isVendorInitiationPendingApprovalStatus(card.initiationStatus) ||
    isVendorInitiationPendingAnnexureApprovalStatus(card.initiationStatus);

  const materialityOpen =
    resolveCurrentJourneyStepIndexFromCard(card) === 1 &&
    String(card.annexure7Status ?? '').toLowerCase() !== 'approved';

  if (!awaitingQueue && !materialityOpen) return null;

  const sentIdx = visuals.findIndex((v) => v === 'sent_for_approval');
  if (sentIdx >= 0) return sentIdx;
  if (isVendorInitiationPendingApprovalStatus(card.initiationStatus)) return 0;
  if (
    isVendorInitiationPendingAnnexureApprovalStatus(card.initiationStatus) ||
    String(card.annexure7Status ?? '').toLowerCase() === 'pending' ||
    materialityOpen
  ) {
    return 1;
  }

  const p = card.pipeline;
  if (p.pendingApprovalRfpCount >= 1) return 2;
  if (p.vpPendingApprovalCount >= 1) return 3;
  if (p.veAwaitingApprovalCount >= 1) return 4;
  if (p.faPendingAtApproverCount >= 1) return 5;

  return resolveCurrentJourneyStepIndexFromCard(card);
}

export function getPreOnboardingDashboardPendingStepLabel(
  stepIndex: number,
  _card: Pick<PreOnboardingInProgressCard, 'pipeline'>,
): string {
  const col = PRE_ONBOARDING_DASHBOARD_TRACKER_COLUMNS[stepIndex];
  if (col) return col.label;
  return JOURNEY_STEPS[stepIndex]?.title ?? 'Pending step';
}

export type PreOnboardingDashboardPendingActivityRow = {
  vendorInitiationId: number;
  vendorLabel: string;
  href: string;
  pendingStepLabel: string;
  daysPending: number;
  department: string;
};

export function buildPreOnboardingDashboardPendingActivityRows(
  items: PreOnboardingInProgressListItem[],
): PreOnboardingDashboardPendingActivityRow[] {
  const rows: PreOnboardingDashboardPendingActivityRow[] = [];
  for (const item of items) {
    const { card, api } = item;
    const visuals = resolveJourneyStepVisualsForCard(card, {
      rawJourneyStepVisuals: api.journey_step_visuals,
    });
    const stepIndex = resolvePreOnboardingPendingActivityStepIndex(card, visuals);
    if (stepIndex == null) continue;
    const updatedAt =
      (typeof api.updated_at === 'string' ? api.updated_at : undefined) ?? card.updatedAt;
    rows.push({
      vendorInitiationId: card.vendorInitiationId,
      vendorLabel: card.serviceName,
      href: getPreOnboardingDashboardVendorHref(card),
      pendingStepLabel: getPreOnboardingDashboardPendingStepLabel(stepIndex, card),
      daysPending: daysPendingSinceIso(updatedAt),
      department: readPreOnboardingRequestingDepartmentName(api),
    });
  }
  rows.sort((a, b) => b.daysPending - a.daysPending || a.vendorLabel.localeCompare(b.vendorLabel));
  return rows;
}

function viQuery(vendorInitiationId: number): string {
  return Number.isFinite(vendorInitiationId) && vendorInitiationId > 0
    ? `vendor_initiation_id=${vendorInitiationId}`
    : '';
}

/** Query flag: RFP module should return to pre-onboarding journey cards, not the RFP list. */
export const PRE_ONBOARDING_JOURNEY_RETURN_PARAM = 'from_pre_onboarding_journey';

/** On journey vendor-evaluation form — resubmit an evaluation returned for NMI. */
export const JOURNEY_VENDOR_EVALUATION_ID_PARAM = 'vendor_evaluation_id';

export function buildPreOnboardingJourneyHref(
  vendorInitiationId: number,
): string {
  return `/vms/pre-onboarding/in-progress/${vendorInitiationId}`;
}

export function isPreOnboardingJourneyReturn(
  searchParams: Pick<URLSearchParams, 'get'>,
): boolean {
  return searchParams.get(PRE_ONBOARDING_JOURNEY_RETURN_PARAM) === '1';
}

/** Passed on `navigate(..., { state })` from pre-onboarding journey step cards. */
export type PreOnboardingJourneyNavState = {
  fromPreOnboardingJourney?: boolean;
  preOnboardingJourneyId?: number;
};

export function isReturnToPreOnboardingJourney(
  searchParams: Pick<URLSearchParams, 'get'>,
  locationState?: unknown,
): boolean {
  if (isPreOnboardingJourneyReturn(searchParams)) return true;
  const st = locationState as PreOnboardingJourneyNavState | null;
  return st?.fromPreOnboardingJourney === true;
}

/** Back target for modules opened from the journey grid (RFP, PV, etc.). */
export function resolvePreOnboardingJourneyExitHref(
  vendorInitiationId: number | null | undefined,
  locationState?: unknown,
): string | null {
  if (vendorInitiationId == null || vendorInitiationId <= 0) return null;
  const st = locationState as PreOnboardingJourneyNavState | null;
  const journeyId = st?.preOnboardingJourneyId;
  if (journeyId != null && journeyId > 0) {
    return buildPreOnboardingJourneyHref(journeyId);
  }
  return buildPreOnboardingJourneyHref(vendorInitiationId);
}

/** Query flag: module should return to My Tray (e.g. deep link from owner tray card). */
export const MY_TRAY_RETURN_PARAM = 'from_my_tray';

export function isMyTrayReturn(searchParams: Pick<URLSearchParams, 'get'>): boolean {
  return searchParams.get(MY_TRAY_RETURN_PARAM) === '1';
}

/** `?vendor_initiation_id=` plus optional journey return flag for deep links from journey tiles. */
export function buildVendorInitiationModuleQuery(
  vendorInitiationId: number,
  options?: { returnToPreOnboardingJourney?: boolean },
): string {
  const parts: string[] = [];
  const viQ = viQuery(vendorInitiationId);
  if (viQ) parts.push(viQ);
  if (options?.returnToPreOnboardingJourney) {
    parts.push(`${PRE_ONBOARDING_JOURNEY_RETURN_PARAM}=1`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/** Open RFP detail from journey when an RFP already exists; otherwise fall back to list/create. */
export function resolveJourneyRfpDetailId(
  pipeline: Pick<
    PreOnboardingInProgressCard['pipeline'],
    | 'rfpId'
    | 'pendingApprovalRfpCount'
    | 'pendingApprovalRfpId'
    | 'approvedRfpCount'
    | 'approvedRfpId'
  >,
): number | null {
  if (pipeline.rfpId != null) return pipeline.rfpId;
  if (pipeline.pendingApprovalRfpCount >= 1 && pipeline.pendingApprovalRfpId != null) {
    return pipeline.pendingApprovalRfpId;
  }
  if (pipeline.approvedRfpCount >= 1 && pipeline.approvedRfpId != null) {
    return pipeline.approvedRfpId;
  }
  return null;
}

export type PreOnboardingJourneyLinkOptions = {
  /** Non–Department RM roles open read-only detail screens instead of create/edit forms. */
  viewOnly?: boolean;
  /** Pre-onboarding journey card id (same as vendor initiation id in current API). */
  journeyId?: number;
};

/**
 * Pre-onboarding RFP tile: draft/NMI → edit form; pending approval / approved / rejected → detail view.
 * View-only users always land on RFP detail when a row exists.
 */
export function resolveJourneyRfpHref(
  vendorInitiationId: number,
  pipeline: Pick<
    PreOnboardingInProgressCard['pipeline'],
    | 'rfpId'
    | 'rfpStatus'
    | 'pendingApprovalRfpCount'
    | 'pendingApprovalRfpId'
    | 'approvedRfpCount'
    | 'approvedRfpId'
  >,
  options?: PreOnboardingJourneyLinkOptions,
): string {
  const moduleQuery = buildVendorInitiationModuleQuery(vendorInitiationId, {
    returnToPreOnboardingJourney: true,
  });
  const rfpId = resolveJourneyRfpDetailId(pipeline);
  if (rfpId == null) {
    if (options?.viewOnly) {
      return buildPreOnboardingJourneyHref(vendorInitiationId);
    }
    return `/vms/rfp-management/create${moduleQuery}`;
  }
  if (options?.viewOnly) {
    return `/vms/rfp-management/${rfpId}${moduleQuery}`;
  }
  const status =
    pipeline.rfpStatus ??
    (pipeline.pendingApprovalRfpCount >= 1
      ? 'pending_approval'
      : pipeline.approvedRfpCount >= 1
        ? 'approved'
        : null);
  if (status) {
    return buildRfpContinueHref({ id: rfpId, status }, vendorInitiationId, {
      returnToPreOnboardingJourney: true,
    });
  }
  return `/vms/rfp-management/${rfpId}${moduleQuery}`;
}

/** Pre-onboarding journey — financial approval form or detail after submit. */
export function resolveJourneyFinancialApprovalHref(
  journeyId: number,
  vendorInitiationId: number,
  pipeline: Pick<
    PreOnboardingInProgressCard['pipeline'],
    'financialApprovalId' | 'financialApprovalDraftId' | 'financialApprovalStatus'
  >,
  options?: PreOnboardingJourneyLinkOptions,
): string {
  const moduleQuery = buildVendorInitiationModuleQuery(vendorInitiationId, {
    returnToPreOnboardingJourney: true,
  });
  const faId = pipeline.financialApprovalId;
  if (faId != null && pipeline.financialApprovalStatus !== 'draft') {
    // Detail for all roles (incl. pending); business owner resubmits via "Edit and resubmit" on detail.
    return `/vms/financial-approval/${faId}${moduleQuery}`;
  }
  // Not started / draft: open create form. Callers pass viewOnly=false for admin so they can
  // review the empty form; CreateFinancialApproval enforces read-only + access (BO / admin).
  // Other view-only roles still bounce to the journey until a submitted FA exists.
  if (options?.viewOnly) {
    return buildPreOnboardingJourneyHref(vendorInitiationId);
  }
  return `/vms/pre-onboarding/in-progress/${journeyId}/financial-approval`;
}

/** Pre-onboarding journey — evaluation detail after submit, or create form when none exists. */
export function resolveJourneyVendorEvaluationHref(
  journeyId: number,
  vendorInitiationId: number,
  pipeline: Pick<
    PreOnboardingInProgressCard['pipeline'],
    'vendorEvaluationId' | 'vendorEvaluationOverallStatus'
  >,
  options?: PreOnboardingJourneyLinkOptions,
): string {
  const moduleQuery = buildVendorInitiationModuleQuery(vendorInitiationId, {
    returnToPreOnboardingJourney: true,
  });
  const evalId = pipeline.vendorEvaluationId;
  if (evalId != null) {
    if (
      !options?.viewOnly &&
      pipeline.vendorEvaluationOverallStatus === STATUS_NEEDS_MORE_INFORMATION
    ) {
      const base = `/vms/pre-onboarding/in-progress/${journeyId}/vendor-evaluation${moduleQuery}`;
      const sep = moduleQuery ? '&' : '?';
      return `${base}${sep}${JOURNEY_VENDOR_EVALUATION_ID_PARAM}=${evalId}`;
    }
    return `/vms/vendor-evaluations/${evalId}${moduleQuery}`;
  }
  if (options?.viewOnly) {
    return buildPreOnboardingJourneyHref(vendorInitiationId);
  }
  return `/vms/pre-onboarding/in-progress/${journeyId}/vendor-evaluation${moduleQuery}`;
}

/** Vendor proposal create/edit form (journey + post–prospective-vendors Finish). */
export function resolveJourneyVendorProposalFormHref(
  vendorInitiationId: number,
  options?: { returnToPreOnboardingJourney?: boolean },
): string {
  return `/vms/vendor-proposals/create${buildVendorInitiationModuleQuery(vendorInitiationId, {
    returnToPreOnboardingJourney: options?.returnToPreOnboardingJourney ?? true,
  })}`;
}

/** Combined PV + VP edit page when both modules exist for this initiation. */
export function buildPreOnboardingPvVpHubHref(
  journeyId: number,
  vendorInitiationId: number,
): string {
  return `/vms/pre-onboarding/in-progress/${journeyId}/prospective-vendors-proposal${buildVendorInitiationModuleQuery(vendorInitiationId, {
    returnToPreOnboardingJourney: true,
  })}`;
}

/** Add / edit prospective vendors form — skips the shortlist list when opened from journey. */
export function resolveJourneyProspectiveVendorsHref(
  vendorInitiationId: number,
  pipeline: Pick<
    PreOnboardingInProgressCard['pipeline'],
    | 'pvSubmittedCount'
    | 'prospectiveVendorDraftId'
    | 'vpSubmittedCount'
    | 'vendorProposalRequestId'
    | 'prospectiveVendorSubmittedId'
    | 'veApprovedCount'
  >,
  options?: PreOnboardingJourneyLinkOptions,
): string {
  const moduleQuery = buildVendorInitiationModuleQuery(vendorInitiationId, {
    returnToPreOnboardingJourney: true,
  });
  const journeyId = options?.journeyId ?? vendorInitiationId;
  const pvId = pipeline.prospectiveVendorSubmittedId;
  const vpId = pipeline.vendorProposalRequestId;
  const hasPv = pipeline.pvSubmittedCount >= 1 || pvId != null;
  const hasVp = pipeline.vpSubmittedCount >= 1 || vpId != null;

  if (options?.viewOnly) {
    if (hasPv && hasVp && pvId != null && vpId != null) {
      return buildPreOnboardingPvVpHubHref(journeyId, vendorInitiationId);
    }
    if (hasVp && vpId != null) {
      return `/vms/vendor-proposals/${vpId}${moduleQuery}`;
    }
    if (hasPv && pvId != null) {
      return `/vms/prospective-vendors/${pvId}${moduleQuery}`;
    }
    return buildPreOnboardingJourneyHref(vendorInitiationId);
  }
  if (hasPv) {
    if (hasVp && vpId != null) {
      return buildPreOnboardingPvVpHubHref(journeyId, vendorInitiationId);
    }
    return resolveJourneyVendorProposalFormHref(vendorInitiationId);
  }
  const draftId = pipeline.prospectiveVendorDraftId;
  if (draftId != null) {
    return `/vms/prospective-vendors/create/${draftId}${moduleQuery}`;
  }
  return `/vms/prospective-vendors/create${moduleQuery}`;
}

/** Navigate from a journey progress tile. */
export function getJourneyStepHref(
  stepKey: JourneyStepKey,
  card: Pick<PreOnboardingInProgressCard, 'vendorInitiationId' | 'id' | 'pipeline'>,
  options?: PreOnboardingJourneyLinkOptions,
): string {
  const viId = card.vendorInitiationId;
  const journeyId = card.id;

  switch (stepKey) {
    case 'vendor_initiation':
      return `/vms/pre-onboarding/in-progress/${journeyId}/vendor-initiation`;
    case 'materiality_assessment':
      return `/vms/vendor-initiation/annexure-7/${viId}?${PRE_ONBOARDING_JOURNEY_RETURN_PARAM}=1`;
    case 'rfp':
      return resolveJourneyRfpHref(viId, card.pipeline, options);
    case 'prospective_vendors_proposal':
      return resolveJourneyProspectiveVendorsHref(viId, card.pipeline, {
        ...options,
        journeyId,
      });
    case 'vendor_evaluation':
      return resolveJourneyVendorEvaluationHref(journeyId, viId, card.pipeline, options);
    case 'financial_approval':
      return resolveJourneyFinancialApprovalHref(journeyId, viId, card.pipeline, options);
    default:
      return `/vms/pre-onboarding/in-progress/${journeyId}`;
  }
}

/** List card → journey progress page. */
export function getPreOnboardingJourneyHref(card: Pick<PreOnboardingInProgressCard, 'id'>): string {
  return `/vms/pre-onboarding/in-progress/${card.id}`;
}

/** Dashboard / tracker link — pending initiation approval opens vendor initiation detail. */
export function getPreOnboardingDashboardVendorHref(
  card: Pick<
    PreOnboardingInProgressCard,
    | 'id'
    | 'initiationStatus'
    | 'vendorInitiationId'
    | 'annexure7Status'
    | 'pipeline'
    | 'financialApprovalComplete'
  >,
): string {
  if (isVendorInitiationPendingApprovalStatus(card.initiationStatus)) {
    return `/vms/vendor-initiation/${card.vendorInitiationId}`;
  }
  if (isVendorInitiationPendingAnnexureApprovalStatus(card.initiationStatus)) {
    return `/vms/vendor-initiation/annexure-7/${card.vendorInitiationId}`;
  }
  if (isVendorInitiationDraftStatus(card.initiationStatus)) {
    return `/vms/vendor-initiation/${card.vendorInitiationId}/edit`;
  }
  if (
    String(card.annexure7Status ?? '').toLowerCase() !== 'approved' &&
    resolveCurrentJourneyStepIndexFromCard(card) === 1
  ) {
    return `/vms/vendor-initiation/annexure-7/${card.vendorInitiationId}`;
  }
  return getPreOnboardingJourneyHref(card);
}

/** Approved pre-onboarding list card → onboarding hub or vendor pipeline when a registered vendor exists. */
export function buildPreOnboardingStartOnboardingHref(
  card: Pick<PreOnboardingInProgressCard, 'vendorInitiationId' | 'vmsRegisteredVendorId'>,
): string {
  const viId = card.vendorInitiationId;
  if (card.vmsRegisteredVendorId != null && card.vmsRegisteredVendorId > 0) {
    return buildOnboardingVendorPipelineHref(card.vmsRegisteredVendorId, viId > 0 ? viId : null);
  }
  if (viId > 0) {
    return `/vms/onboarding?vendor_initiation_id=${viId}`;
  }
  return '/vms/onboarding';
}

/** FA reached approval at least once (including post-approval amend / re-approval). */
export function isPreOnboardingFinancialApprovalCompleteForRegister(
  faStatus: string | null | undefined,
): boolean {
  const st = String(faStatus ?? '').toLowerCase();
  return st === 'approved' || st === STATUS_PENDING_REAPPROVAL;
}

/**
 * Hide from Pre Onboarding In Progress when pre-onboarding and onboarding are both done.
 * Keep visible during FA re-approval (same as list card chips).
 */
export function shouldHidePreOnboardingInProgressListCard(
  card: Pick<
    PreOnboardingInProgressCard,
    'financialApprovalComplete' | 'pipeline' | 'vmsRegisteredVendorId'
  >,
  onboardingCompleteByRegId: Record<number, boolean>,
): boolean {
  if (isFinancialApprovalReapprovalInProgress(card)) return false;
  if (!card.financialApprovalComplete) return false;
  const regId = card.vmsRegisteredVendorId;
  if (regId == null || regId < 1) return false;
  return onboardingCompleteByRegId[regId] === true;
}
